/**
 * FullCalendar-engine implementation of the calendar keyboard contract
 * ("termin" scope).
 *
 * WHAT the keys do is specified ONCE in ./keyboardSpec.ts (single source of
 * truth for both calendar engines during the migration period) — this file is
 * HOW this engine does it. Its conformance declaration lives in
 * ./keyboardConformance.ts; the MobiliCalendar engine declares its own in
 * molecules/MobiliCalendar/keyboardConformance.ts.
 *
 * Modifier+arrow combos were deliberately REJECTED for date navigation: the
 * provider never dispatches arrows (navigation keys), and every modifier is
 * taken somewhere — Ctrl+←/→ = macOS Mission Control spaces, Alt+←/→ =
 * browser back/forward (Win/Linux), Cmd+←/→ = browser back/forward (macOS),
 * Ctrl+Alt+arrows = Intel display rotation (Windows).
 *
 * Arrows and Escape CANNOT go through the central hotkey provider: it treats them
 * as navigation keys (buffer-clear + return) and never dispatches them, so they
 * live as raw keydown listeners (arrows + i/j/k/l cell cursor incl. Shift+i/k →
 * CellNavigation.ts, Escape staged clear → EventShelf.tsx); only the SHIFTED
 * J/L page-navigation combos are registered here. Everything else MUST be
 * registered here so the "?" help overlay, scope rules and conflict resolution
 * stay accurate.
 *
 * Sequences vs. single keys: the provider matches the LONGEST sequence first
 * (then priority), so global "g …" navigation chords (g a, g p, …) reliably
 * beat the single-key bindings above — no letters need to be avoided here.
 *
 * Registration pattern: the Scheduler keeps all three calendar variants (standard,
 * timesheet, single-therapist) MOUNTED and toggles them with display:none, so each
 * binding is registered once per instance. Every binding therefore carries an
 * `isActiveInstance` (visibility) guard — without it, whichever hidden instance
 * registered first would swallow the key. Handlers read all state lazily through
 * `depsRef.current`, so registrations never go stale.
 */
import { useEffect, MutableRefObject } from "react";
import type { CalendarApi } from "@fullcalendar/core";
import type { EventReceiveArg } from "@fullcalendar/interaction";
import moment from "moment";
import { useHotkeys as useHotkeysContext } from "../../../hotkeys/HotkeysProvider";
import type { Hotkey } from "../../../hotkeys/types";
import notificationStore from "../../../stores/Notification/notificationStore";
import type CalendarStore from "../../../stores/Calendar/calendarStore";
import type { PatientDto } from "../../../api-client";
import type { CalendarEvent, ClientTimeSlot } from "../controller/adapters";
import {
  adaptAppointmentToDraggableEvent,
  adaptTimeSlotToDraggableEvent,
  isCancelledAppointment,
} from "../controller/adapters";
import {
  eventToCalendarEvent,
  materializeCalendarEvent,
} from "../controller/adapters/display-to-client/reverse.adapter";
import type { ContextMenuControllerRef } from "../ContextMenu/ContextMenuWithController";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import { buildDataEvent } from "../DynamicTemplateProvider/DynamicTemplateProvider";
import type { TemplateHandle } from "../DynamicTemplateProvider/types";
import type { CalendarDateNavigation } from "../HeaderToolbar";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { CALENDAR_KEYBOARD_SPEC } from "./keyboardSpec";
import { slotTherapy } from "../../../helpers/slotTherapy";

/** Shelf configuration slice needed to resolve the selected shelf item. */
export type ShelfData = {
  appointments: any[];
  patient?: PatientDto;
  mode?: string;
  visible?: boolean;
};

/**
 * The engine-agnostic slice of the deps: everything the SHARED shelf-selection
 * logic (resolve payload, "a" cycling, selection continuity) needs. Both
 * engines satisfy this — the FullCalendar engine through CalendarHotkeyDeps,
 * the MobiliCalendar bridge directly (useMobiliCalendarHotkeys).
 */
export type ShelfSelectionDeps = {
  calendarStore: CalendarStore;
  /** All shelf configs (unscheduled + pinboard), including invisible ones. */
  getShelfData: () => ShelfData[];
  /** Template-widget snapshot for the "a" source cycling. */
  getTemplateData: () => { visible: boolean; handles: TemplateHandle[] };
};

/**
 * Everything a calendar instance must supply for the bindings to operate.
 * Handlers evaluate these lazily (at keypress time) through a ref the Calendar
 * component reassigns on every render, so no value here can go stale.
 */
export type CalendarHotkeyDeps = {
  /** Visibility guard — false while this instance is hidden via display:none. */
  isActiveInstance: () => boolean;
  calendarStore: CalendarStore;
  calendarApi: () => CalendarApi | null | undefined;
  contextMenu: () => ContextMenuControllerRef | null;
  /** Appointment the cell cursor last stopped on (CellNavigation). */
  getSelectedAppointment: () => ClientTimeSlot | null;
  /** Current cell cursor position (resource column + "HH:mm" slot time). */
  getFocusedCell: () => { resourceId: string; timeStr: string } | null;
  /** Date this calendar instance is displaying. */
  getDate: () => Date;
  /** All shelf configs (unscheduled + pinboard), including invisible ones. */
  getShelfData: () => ShelfData[];
  /**
   * The full external-drop pipeline (create-permission, frequency, credentials,
   * duplicate-day checks, then controller dispatch). Resolves true when the
   * event was accepted and handed to the controller.
   */
  receiveExternalEvent: (arg: EventReceiveArg) => Promise<boolean>;
  toggleAttended: (slot: ClientTimeSlot) => void;
  navigateToPatient: (patientId: number, rxId: string | null, subtab: PATIENT_SUBTABS) => void;
  /** Opens the AddEventModal pre-filled for the cursor cell (the "f" action). */
  openQuickCreateAtCursor: () => void;
  /** Template-widget snapshot for the "a" source cycling. */
  getTemplateData: () => { visible: boolean; handles: TemplateHandle[] };
  /** Toolbar date navigation (j/k/J/K); null until the toolbar has mounted. */
  dateNavigation: () => CalendarDateNavigation | null;
};

/** Parse the FullCalendar "HH:mm:ss" duration format into minutes. */
export const durationToMinutes = (duration?: string): number => {
  if (!duration) return 0;
  const [hh = "0", mm = "0", ss = "0"] = duration.split(":");
  const minutes = Number(hh) * 60 + Number(mm) + Math.round(Number(ss) / 60);
  return Number.isFinite(minutes) ? minutes : 0;
};

/**
 * Group signature of an "Offene Termine" card. MUST mirror the active-pass key
 * of groupAppointmentsByRx in EventShelf.tsx: a card is a STACK of same-group
 * appointments keyed to its first appointment, so after one is scheduled the
 * card re-keys to the group's next appointment — selection continuity means
 * following that re-key.
 */
const shelfGroupKeyOf = (appointment: any): string => {
  const therapyRxIds =
    appointment?.timeSlots?.reduce((acc: string, slot: any) => {
      if (slot.therapyRx?.id) return acc + slot.therapyRx.id;
      const therapyId = slotTherapy(slot)?.id;
      if (therapyId) return acc + `therapy-${therapyId}`;
      return acc;
    }, "") || "";
  const addressId = appointment?.address?.id || "unknown-address";
  // Same predicate as the shelf: any unresolved cancellation, either origin.
  const isCancelled = !!appointment && isCancelledAppointment(appointment);
  return `${isCancelled ? "cancelled-" : ""}${therapyRxIds}-${addressId}`;
};

/** Address payload for a shelf selection (mirrors EventShelfController.getAddressPayload). */
const shelfAddressOf = (
  appointment: any
): { id: string; latitude: number; longitude: number } | null => {
  const coordinates = appointment?.address?.coordinates;
  return coordinates?.latitude != null && coordinates?.longitude != null
    ? {
        id: appointment.address.id,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      }
    : null;
};

/**
 * Resolve the currently selected shelf item into the SAME drag payload the
 * mouse path serializes into `data-event` attributes, so keyboard insertion is
 * payload-identical to drag-and-drop:
 * - Vorlage (Dynamic Template Provider): stored payload (isTemplate + template).
 * - Ablage slot pin ("Umziehen → Nur diese"): single-slot payload with the host
 *   appointment id restored + isScheduledSlotMove for the atomic reschedule verb.
 * - Offene Termine / whole-appointment pin: whole-appointment payload.
 */
export type ResolvedShelfSelection =
  | { kind: "template"; payload: Record<string, any> }
  | {
      kind: "appointment";
      payload: Record<string, any>;
      /**
       * Successor for selection auto-advance, captured BEFORE the insert while
       * the list still contains the scheduled item — the caches update
       * asynchronously, so this snapshot is the only reliable source.
       * - Offene Termine: next appointment of the SAME card (group stack).
       * - Ablage: positional successor (the pin that takes the placed pin's spot).
       */
      nextCandidate: any | null;
    };

export const resolveShelfSelectionPayload = (
  deps: ShelfSelectionDeps
): ResolvedShelfSelection | null => {
  const store = deps.calendarStore;
  if (store.selectedShelfTemplate) {
    return { payload: store.selectedShelfTemplate, kind: "template" };
  }
  const selectedId = store.selectedShelfAppointmentId;
  if (!selectedId) return null;

  for (const shelf of deps.getShelfData()) {
    const appointments: any[] = shelf.appointments || [];
    const item = appointments.find((a: any) => a?.id === selectedId);
    if (!item) continue;
    const resolvedPatient =
      shelf.patient || (item.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto);
    const nextCandidate = (() => {
      if (shelf.mode === "unscheduled") {
        // Successor within the same card: first OTHER appointment sharing the
        // group signature, in list order (= the group's next first appointment).
        const groupKey = shelfGroupKeyOf(item);
        return (
          appointments.find((a: any) => a?.id !== selectedId && shelfGroupKeyOf(a) === groupKey) ??
          null
        );
      }
      if (shelf.mode === "pinboard") {
        // Pins are heterogeneous (moves of unrelated appointments/slots), so
        // the successor is positional: after the placed pin is removed, the
        // next pin slides into its spot — advance the outline with it.
        const idx = appointments.findIndex((a: any) => a?.id === selectedId);
        return appointments[idx + 1] ?? appointments[idx - 1] ?? null;
      }
      return null;
    })();
    if ((item as any).isSlotPin) {
      return {
        kind: "appointment",
        nextCandidate,
        payload: {
          ...adaptTimeSlotToDraggableEvent(
            { ...item, id: (item as any).pinnedAppointmentId },
            item.timeSlots![0]!,
            resolvedPatient
          ),
          isScheduledSlotMove: true,
        },
      };
    }
    return {
      kind: "appointment",
      nextCandidate,
      payload: adaptAppointmentToDraggableEvent(item, resolvedPatient, true) as any,
    };
  }
  return null;
};

/**
 * Plan a resolved draggable payload at the current cell cursor. Synthesizes the
 * exact event a physical drop would create (payload fields on extendedProps)
 * and pushes it through the SAME validation + controller pipeline — no
 * keyboard-only side paths, so behavior can never diverge from drag-and-drop.
 * Returns true when the pipeline accepted and handed the event to the controller.
 */
const planPayloadAtCursor = async (
  deps: CalendarHotkeyDeps,
  payload: Record<string, any>
): Promise<boolean> => {
  const api = deps.calendarApi();
  if (!api) return false;
  const cursor = deps.getFocusedCell();
  if (!cursor) {
    notificationStore.showMessage(
      "Keine Zelle ausgewählt — mit den Pfeiltasten eine Kalenderzelle wählen.",
      "info"
    );
    return false;
  }
  const minutes = durationToMinutes(payload.duration);
  if (!minutes) {
    notificationStore.showMessage("Termin hat keine gültige Dauer.", "warning");
    return false;
  }
  const store = deps.calendarStore;
  // In single-therapist view the resource columns ARE dates; otherwise the
  // column is a therapist and the date comes from the calendar's current day.
  const dateStr = store.singleTherapistView
    ? cursor.resourceId
    : moment(deps.getDate()).format("YYYY-MM-DD");
  const start = new Date(`${dateStr}T${cursor.timeStr}:00`);
  const end = new Date(start.getTime() + minutes * 60_000);

  // Mirror how FullCalendar maps a Draggable payload: id/title/duration are
  // standard fields, everything else lands in extendedProps. The container
  // background matches real appointment cards so the provisional event does not
  // flash FullCalendar-default blue while the validations run — the optimistic
  // cache patch then swaps it for the fully rendered event in place.
  const { id, title, duration: _duration, ...extendedProps } = payload;
  const event = api.addEvent({
    id,
    title: title ?? "",
    start,
    end,
    resourceId: cursor.resourceId,
    color: mobiliTheme.lowLevelContainerBackground,
    extendedProps,
  });
  if (!event) return false;

  return deps.receiveExternalEvent({ event } as unknown as EventReceiveArg);
};

/**
 * Shared selection continuity after a successful shelf placement (single store
 * write — only shelf observers re-render):
 * - Template: KEEP selected. Its identity is the handle (not a concrete
 *   appointment), so the user can keep planning slots with repeated b. The
 *   chip's own lifecycle clears/refreshes the selection when the handle
 *   vanishes or changes (see HandleChip in DynamicTemplateProvider).
 * - Offene Termine: advance to the next appointment of the SAME card. The
 *   card is a group stack keyed to its first appointment; scheduling one
 *   re-keys the card, and this keeps the outline (and the b target) on it.
 * - Ablage pin: advance to the positional successor so repeated b drains the
 *   pinboard. removeFromPinBoard cleared the selection DURING the awaited
 *   pipeline; this post-await write intentionally wins.
 */
export const advanceShelfSelectionAfterPlacement = (
  store: CalendarStore,
  resolved: ResolvedShelfSelection
): void => {
  if (resolved.kind === "template") return;
  const next = resolved.nextCandidate;
  if (next?.id) store.setSelectedShelfAppointment(next.id, shelfAddressOf(next));
  else store.setSelectedShelfAppointment(null, null);
};

/**
 * "Belegen" (b): plan the currently selected shelf item (Vorlage / Offene
 * Termine card / selected Ablage pin) at the cell cursor.
 */
export const insertShelfSelectionAtCursor = async (deps: CalendarHotkeyDeps): Promise<void> => {
  const resolved = resolveShelfSelectionPayload(deps);
  const store = deps.calendarStore;
  if (!resolved) {
    // Stale selection (item vanished from the shelf, e.g. scheduled elsewhere).
    store.setSelectedShelfAppointment(null, null);
    notificationStore.showMessage("Keine Auswahl im Regal.", "info");
    return;
  }

  const accepted = await planPayloadAtCursor(deps, resolved.payload);
  if (!accepted) return; // pipeline already removed the provisional event + notified

  advanceShelfSelectionAfterPlacement(store, resolved);
};

/**
 * Resolve a specific pinboard item (whole-appointment pin or "Nur diese" slot
 * pin) into the drag payload, independent of the shelf selection. Used by the
 * "v" (Umziehen) place step so v owns the pinboard while b owns the selection.
 */
export const resolvePinboardItemPayload = (item: any): { payload: Record<string, any> } | null => {
  if (!item) return null;
  const patient = item.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto | undefined;
  if (item.isSlotPin) {
    return {
      payload: {
        ...adaptTimeSlotToDraggableEvent(
          { ...item, id: item.pinnedAppointmentId },
          item.timeSlots![0]!,
          patient
        ),
        isScheduledSlotMove: true,
      },
    };
  }
  return { payload: adaptAppointmentToDraggableEvent(item, patient, true) as any };
};

/**
 * "Umziehen" place (v on an empty cell): plan a specific pinboard item at the
 * cursor through the same pipeline as a drop. The controller's onEventReceive
 * removes the placed item from the pinboard on success — no selection continuity
 * here (v deliberately leaves the b-selection untouched).
 */
const placePinboardItemAtCursor = async (deps: CalendarHotkeyDeps, item: any): Promise<void> => {
  const resolved = resolvePinboardItemPayload(item);
  if (!resolved) {
    notificationStore.showMessage("Kein Regal-Termin zum Einplanen.", "info");
    return;
  }
  await planPayloadAtCursor(deps, resolved.payload);
};

/**
 * "Umziehen" grab (v on an appointment): lift the appointment under the cursor
 * onto the pinboard through the same controller path as the context-menu action
 * (event removal by groupId, group-participant handling, pinboard add). Prefers
 * the LIVE FullCalendar event so the move flow sees full extendedProps
 * (timeSlotType, participants, …); falls back to a minimal synthetic event.
 *
 * `moveSingleSlot` is the keyboard counterpart of the context menu's
 * "Nur diese" dialog choice (Shift+V): only the slot under the cursor is
 * lifted; plain v always grabs the WHOLE appointment.
 *
 * The controller auto-selects the freshly pinned item as the shelf selection
 * (a legacy convenience for the old single-verb keyboard flow). Under the b/v
 * split that would clobber the item the user has queued for "b", so we capture
 * the current selection before the grab and restore it immediately after — the
 * pinboard is v's domain, the selection stays b's.
 */
const liftCursorAppointmentToShelf = (deps: CalendarHotkeyDeps, moveSingleSlot: boolean): void => {
  const slot = deps.getSelectedAppointment();
  if (!slot) return;
  // Only real patient appointments can be lifted onto the pinboard (mirrors the
  // mouse context menu, which offers "Umziehen" only for appointment events).
  // Standalone timeslots (EventTimeSlot, LeaveOfAbsence, LunchBreak, working
  // hours, …) carry no appointmentId; letting them through would sweep EVERY
  // groupId-less event off the calendar in handleMoveEvent (they all share
  // FullCalendar's normalized "" groupId) and then pin nothing — the shelf
  // move flow resolves items via lookupByAppointmentId.
  if (!slot.appointmentId) {
    notificationStore.showMessage("Nur Termine können ins Regal verschoben werden.", "info");
    return;
  }
  const contextMenu = deps.contextMenu();
  if (!contextMenu) return;
  const live = slot.id ? deps.calendarApi()?.getEventById(String(slot.id)) : null;

  const store = deps.calendarStore;
  // Snapshot the b-selection so the grab's auto-select can't disturb it.
  const prevTemplate = store.selectedShelfTemplate;
  const prevAppointmentId = store.selectedShelfAppointmentId;
  const prevAddress = store.selectedShelfAppointmentAddress;
  const calendarEvent: CalendarEvent = live
    ? eventToCalendarEvent(live as any)
    : ({
        id: slot.id,
        groupId: slot.appointmentId,
        extendedProps: {
          appointmentId: slot.appointmentId,
          timeSlot: slot,
          patient: slot.patient,
        },
      } as any);
  // The live branch of calendarEvent is a FullCalendar EventApi whose
  // id/groupId are prototype getters — materialize before spreading, or the
  // single-slot flag would ride on an event without a slot id.
  const singleSlotEvent = () => {
    const plainEvent = materializeCalendarEvent(calendarEvent);
    return {
      ...plainEvent,
      extendedProps: { ...plainEvent.extendedProps, moveSingleSlot: true },
    };
  };
  contextMenu.moveEventToShelf(moveSingleSlot ? singleSlotEvent() : calendarEvent);
  // Restore the pre-grab b-selection (the grab ran synchronously up to its
  // setSelectedShelfAppointment call). Respect the store's exclusive template ⇄
  // appointment invariant by restoring the one that was actually set.
  if (prevTemplate) {
    store.setSelectedShelfTemplate(prevTemplate);
  } else {
    store.setSelectedShelfAppointment(prevAppointmentId, prevAddress);
  }
};

/**
 * "Umziehen" (v / Shift+V): a single verb for the whole move flow. On a
 * patient appointment it GRABS it onto the pinboard — plain v the WHOLE
 * appointment, Shift+V only the slot under the cursor ("Nur diese"; the
 * context menu offers the same choice as a dialog). On an empty cell either
 * combo PLACES the most recent pinboard item there. This lets a keyboard user
 * grab an obstacle (v), re-place it elsewhere (v), and separately plan their
 * b-selection — without ever lifting their hands from the keyboard.
 */
const performUmziehen = (deps: CalendarHotkeyDeps, moveSingleSlot = false): void => {
  const slot = deps.getSelectedAppointment();
  // Cursor on a real patient appointment → grab it onto the pinboard.
  if (slot?.appointmentId) {
    liftCursorAppointmentToShelf(deps, moveSingleSlot);
    return;
  }
  // Cursor on an empty cell → place the most recent pinboard item (LIFO: the
  // just-grabbed obstacle first).
  const pins = deps.calendarStore.pinBoardItems;
  if (pins && pins.length > 0) {
    void placePinboardItemAtCursor(deps, pins[pins.length - 1]);
    return;
  }
  notificationStore.showMessage(
    "Nichts zum Verschieben — Cursor auf einen Termin (heben) oder auf eine freie Zelle bei gefülltem Regal (einplanen).",
    "info"
  );
};

/**
 * "a" — cycle the shelf selection across EVERY selectable element, in visual
 * order: each Vorlage handle → each "Offene Termine" group card → each Ablage
 * pin (wrapping). Unlike a source-level cycle, multiple templates, multiple
 * unscheduled group cards and multiple pins each get their own stop, so the
 * user can reach any shelf item from the keyboard. b/v then plans it at the
 * cell cursor. Single store write per press.
 */
type ShelfCycleEntry = {
  kind: "template" | "appointment";
  /** Match key for locating the current selection: `template:<handleId>` or the appointment id. */
  id: string;
  /** Group signature for unscheduled cards, so a selection on any group member maps back to its card. */
  groupKey?: string;
  select: () => void;
};

/**
 * Ordered, de-duplicated list of the "Offene Termine" group cards (one entry
 * per card, represented by its first appointment) — mirrors the shelf render
 * order where cancelled groups come before active ones.
 */
const orderedUnscheduledCards = (appointments: any[]): any[] => {
  const seen = new Set<string>();
  const cards: any[] = [];
  const collect = (wantCancelled: boolean) => {
    for (const appt of appointments) {
      if (!appt) continue;
      const isCancelled = isCancelledAppointment(appt);
      if (isCancelled !== wantCancelled) continue;
      const key = shelfGroupKeyOf(appt);
      if (seen.has(key)) continue;
      seen.add(key);
      cards.push(appt);
    }
  };
  collect(true); // cancelled groups first
  collect(false); // then active groups
  return cards;
};

export const cycleShelfSelectionSource = (deps: ShelfSelectionDeps): void => {
  const store = deps.calendarStore;
  const template = deps.getTemplateData();
  const shelves = deps.getShelfData();
  const unscheduled = shelves.find((s) => s.mode === "unscheduled" && s.visible !== false);
  const pinboard = shelves.find((s) => s.mode === "pinboard" && s.visible !== false);

  const entries: ShelfCycleEntry[] = [];

  // 1. Vorlagen — one stop per template handle.
  if (template.visible) {
    for (const handle of template.handles) {
      entries.push({
        kind: "template",
        id: `template:${handle.id}`,
        select: () =>
          store.setSelectedShelfTemplate(
            buildDataEvent(handle, store.selectedPatientId ?? undefined) as any
          ),
      });
    }
  }

  // 2. Offene Termine — one stop per group card (keyed to the card's first appt).
  if (unscheduled) {
    for (const appt of orderedUnscheduledCards(unscheduled.appointments || [])) {
      entries.push({
        kind: "appointment",
        id: appt.id,
        groupKey: shelfGroupKeyOf(appt),
        select: () => store.setSelectedShelfAppointment(appt.id, shelfAddressOf(appt)),
      });
    }
  }

  // 3. Ablage — one stop per pin (pins are heterogeneous, so no grouping).
  if (pinboard) {
    for (const pin of pinboard.appointments || []) {
      if (!pin) continue;
      entries.push({
        kind: "appointment",
        id: pin.id,
        select: () => store.setSelectedShelfAppointment(pin.id, shelfAddressOf(pin)),
      });
    }
  }

  if (entries.length === 0) {
    notificationStore.showMessage(
      "Keine Auswahl möglich — weder Vorlagen noch offene Termine noch Ablage vorhanden.",
      "info"
    );
    return;
  }

  // Locate the CURRENT selection in the flat list (-1 = none → start at the first).
  let current = -1;
  if (store.selectedShelfTemplate) {
    const tid = store.selectedShelfTemplate.id;
    current = entries.findIndex((e) => e.kind === "template" && e.id === tid);
  } else if (store.selectedShelfAppointmentId) {
    const id = store.selectedShelfAppointmentId;
    current = entries.findIndex((e) => e.kind === "appointment" && e.id === id);
    if (current === -1 && unscheduled) {
      // The selection may point at a non-first member of an unscheduled group
      // card (selection auto-advances within a group); map it back to the card.
      const selected = (unscheduled.appointments || []).find((a: any) => a?.id === id);
      if (selected) {
        const gk = shelfGroupKeyOf(selected);
        current = entries.findIndex((e) => e.kind === "appointment" && e.groupKey === gk);
      }
    }
  }

  entries[(current + 1) % entries.length].select();
};

/**
 * Build the full binding table. All bindings live in scope "termin" and read
 * state through `depsRef` at keypress time.
 */
const buildCalendarHotkeys = (depsRef: MutableRefObject<CalendarHotkeyDeps>): Hotkey[] => {
  const deps = () => depsRef.current;
  const active = () => !!depsRef.current?.isActiveInstance();
  const hasShelfSelection = () => {
    const store = depsRef.current?.calendarStore;
    return !!(store?.selectedShelfTemplate || store?.selectedShelfAppointmentId);
  };

  return [
    // ---- b = Belegen (plan the b-selection) / v = Umziehen (grab & place) ----
    // b and v are now DISTINCT verbs:
    //  • b plans whatever shelf item is selected (Vorlage / Offene Termine /
    //    a pin selected via "a"); with no selection it edits the appointment
    //    under the cursor.
    //  • v is the Umziehen verb only: on a patient appointment it grabs the
    //    WHOLE appointment onto the pinboard (leaving the b-selection intact);
    //    Shift+V grabs only the slot under the cursor ("Nur diese"); on an
    //    empty cell either combo places the most recent pinboard item. So a
    //    user can grab an obstacle (v), re-place it (v) and plan their
    //    selection (b) hands-on-keyboard.
    {
      combo: "b",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.belegen.description,
      when: active,
      handler: () => {
        const d = deps();
        if (hasShelfSelection() && !d.calendarStore.timeSheetMode) {
          void insertShelfSelectionAtCursor(d);
          return;
        }
        const slot = d.getSelectedAppointment();
        if (slot) d.contextMenu()?.handleEditAppointment(slot);
      },
    },
    {
      combo: "v",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.umziehen.description,
      when: () => active() && !depsRef.current.calendarStore.timeSheetMode,
      handler: () => performUmziehen(deps()),
    },
    {
      combo: "V",
      scope: "termin",
      priority: 150,
      description:
        "Verschieben (nur diese Behandlung): einzelne Behandlung unter dem Cursor ins Regal heben",
      when: () => active() && !depsRef.current.calendarStore.timeSheetMode,
      handler: () => performUmziehen(deps(), true),
    },
    // ---- Cursor-appointment actions (checks stay inside handlers so these
    // ---- registrations shadow same-key global hotkeys exactly as before) ----
    {
      combo: "ü",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openOverview.description,
      when: active,
      handler: () => {
        const slot = deps().getSelectedAppointment();
        const patientId = slot?.patient?.id;
        if (patientId) {
          deps().contextMenu()?.handleAppointmentOverview(patientId, slot?.rx?.id);
        }
      },
    },
    {
      combo: "w",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.markAttended.description,
      when: active,
      handler: () => {
        const slot = deps().getSelectedAppointment();
        if (slot) deps().toggleAttended(slot);
      },
    },
    {
      combo: "r",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openPrescriptions.description,
      when: active,
      handler: () => {
        const slot = deps().getSelectedAppointment();
        const patientId = slot?.patient?.id;
        if (patientId) {
          deps().navigateToPatient(patientId, slot?.rx?.id ?? null, PATIENT_SUBTABS.PRESCRIPTIONS);
        }
      },
    },
    {
      combo: "p",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openPatientAppointments.description,
      when: active,
      handler: () => {
        const slot = deps().getSelectedAppointment();
        const patientId = slot?.patient?.id;
        if (patientId) {
          deps().navigateToPatient(patientId, null, PATIENT_SUBTABS.APPOINTMENTS);
        }
      },
    },
    {
      combo: "n",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openNotes.description,
      when: active,
      handler: () => {
        if (deps().getSelectedAppointment()) deps().contextMenu()?.onOpenNotesDialog();
      },
    },
    {
      combo: "x",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.cancelAppointment.description,
      when: active,
      handler: () => {
        if (deps().getSelectedAppointment()) deps().contextMenu()?.onCancelAppointment();
      },
    },
    {
      combo: "f",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.quickCreate.description,
      when: active,
      handler: () => deps().openQuickCreateAtCursor(),
    },
    // ---- Selection-source cycling & date navigation ----
    {
      combo: "a",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.cycleShelfSelection.description,
      when: () => active() && !depsRef.current.calendarStore.timeSheetMode,
      handler: () => cycleShelfSelectionSource(deps()),
    },
    {
      combo: "q",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.togglePatientTab.description,
      when: active,
      handler: () => deps().calendarStore.toggleSelectedPatient(),
    },
    {
      combo: "J",
      scope: "termin",
      priority: 150,
      description: `${CALENDAR_KEYBOARD_SPEC.datePage.description} — zurück`,
      when: active,
      handler: () => deps().dateNavigation()?.previousDay(),
    },
    {
      combo: "L",
      scope: "termin",
      priority: 150,
      description: `${CALENDAR_KEYBOARD_SPEC.datePage.description} — vor`,
      when: active,
      handler: () => deps().dateNavigation()?.nextDay(),
    },
  ];
};

/**
 * Register the calendar binding table for one calendar instance. Registers once
 * per mount; handlers stay fresh by reading through `depsRef`.
 */
export const useCalendarHotkeys = (depsRef: MutableRefObject<CalendarHotkeyDeps>): void => {
  const { register } = useHotkeysContext();
  useEffect(() => {
    const registrations = buildCalendarHotkeys(depsRef).map(register);
    return () => registrations.forEach((registration) => registration.unregister());
  }, [register, depsRef]);
};
